import { HTMLAttributes, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import apiClient from '@/lib/axios'
import { useAuthStore } from '@/stores/authStore'
import { toast } from '@/hooks/use-toast'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'

type UserAuthFormProps = HTMLAttributes<HTMLDivElement>

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Lütfen e-posta adresinizi giriniz' })
    .email({ message: 'Geçersiz e-posta adresi' })
    .refine(
      (email) => {
        const domain = email.split('@')[1]?.toLowerCase();
        const allowedDomains = ['gmail.com', 'hotmail.com', 'outlook.com', 'ixcloud.com'];
        return domain && allowedDomains.includes(domain);
      },
      {
        message: 'Sadece Gmail, Hotmail, Outlook veya iCloud e-posta adresleri kullanılabilir',
      }
    ),
  password: z
    .string()
    .min(1, {
      message: 'Lütfen şifrenizi giriniz',
    })
    .min(7, {
      message: 'Şifre en az 7 karakter olmalıdır',
    }),
})

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const { setAccessToken, setUser } = useAuthStore((state) => state.auth)
  const navigate = useNavigate()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)
    try {
      const response = await apiClient.post('/giris', {
        eposta: data.email,
        sifre: data.password,
      })

      // Token'ı store'a kaydet
      if (response.data.token) {
        setAccessToken(response.data.token)
        
        // User bilgisini de kaydet (eğer varsa)
        // API response'da 'ogrenci' olarak dönüyor
        if (response.data.ogrenci) {
          setUser(response.data.ogrenci)
          console.log('Kullanıcı bilgisi kaydedildi:', response.data.ogrenci)
        } else {
          console.warn('API response\'da ogrenci bilgisi yok:', response.data)
        }

        toast({
          title: 'Giriş Başarılı',
          description: 'Hoş geldiniz!',
        })

        // Ana sayfaya yönlendir - router navigate kullanarak basepath'i koru
        navigate({ to: '/' })
      } else {
        console.error('API response\'da token yok:', response.data)
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Giriş Hatası',
        description: error.response?.data?.error || error.response?.data?.message || 'Giriş yapılırken bir hata oluştu',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>E-Posta</FormLabel>
                  <FormControl>
                    <Input placeholder='*******@*******.com' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Şifre</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder='Şifrenizi giriniz' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className='mt-2' disabled={isLoading}>
              Giriş Yap
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
